import {
  Alert,
  Backdrop,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios.js";

import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";

import usePageTitle from "app/pageTitle";
import { useAwaitDialog } from "app/components/status_dialog";

function WhatsAppBroadcastsPage() {
  const pageTitle = "WhatsApp - Broadcasts";
  usePageTitle(pageTitle);

  const navigate = useNavigate();
  const { ref } = useParams();

  const statusDialog = useAwaitDialog();

  const [showLoading, setShowLoading] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [business, setBusiness] = useState({});
  const [broadcasts, setBroadcasts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [businessNumbers, setBusinessNumbers] = useState([]);

  const [open, setOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [severity, setSeverity] = useState("error");

  const [createOpen, setCreateOpen] = useState(false);

  const [title, setTitle] = useState("");
  const [templateReference, setTemplateReference] = useState("");
  const [businessNumberReference, setBusinessNumberReference] = useState("");

  const [search, setSearch] = useState("");

  const handleClose = () => {
    setOpen(false);
    setErrorMsg("");
  };

  const fetchData = async () => {
    setShowLoading(true);

    await axios
      .get(`/portal/wacc/${ref}/broadcasts`)
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setBusiness(result.data.data.business || {});
          setBroadcasts(result.data.data.broadcasts || []);
          setTemplates(result.data.data.templates || []);
          setBusinessNumbers(result.data.data.business_numbers || []);
          setPageLoaded(true);
        } else {
          setErrorMsg(result.data.data.message);
          setSeverity("error");
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setErrorMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setSeverity("error");
        setOpen(true);
      });
  };

  useEffect(() => {
    fetchData();
  }, [ref]);

  const handleCreate = async () => {
    if (!title.trim()) {
      setErrorMsg("Broadcast name is required");
      setSeverity("error");
      setOpen(true);
      return;
    }

    if (!templateReference) {
      setErrorMsg("Select a template");
      setSeverity("error");
      setOpen(true);
      return;
    }

    if (!businessNumberReference) {
      setErrorMsg("Select a business number");
      setSeverity("error");
      setOpen(true);
      return;
    }

    setShowLoading(true);
    setCreateOpen(false);

    const json = {
      reference: ref,
      title: title.trim(),
      template_reference: templateReference,
      business_number_reference: businessNumberReference,
    };
    console.log(json);

    await axios
      .post("/portal/wacc/broadcasts/create.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          const broadcast = result.data.data.broadcast;

          setCreateOpen(false);
          setTitle("");
          setTemplateReference("");
          setBusinessNumberReference("");

          navigate(`/wacc/broadcasts/${broadcast.reference}/view`);
        } else {
          setErrorMsg(result.data.data.message);
          setSeverity("error");
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setErrorMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setSeverity("error");
        setOpen(true);
      });
  };

  const filteredBroadcasts = broadcasts.filter((broadcast) => {
    const value = search.toLowerCase();

    return (
      broadcast.title?.toLowerCase().includes(value) ||
      broadcast.reference?.toLowerCase().includes(value)
    );
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      {pageLoaded && (
        <MDBox mb={5}>
          {/* HEADER */}
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <MDBox>
              <MDTypography variant="h5">Broadcasts</MDTypography>

              <MDTypography variant="body2" color="text" sx={{ mt: 0.5 }}>
                {business.title}
              </MDTypography>
            </MDBox>

            <MDBox display="flex" gap={2}>
              <MDButton
                variant="outlined"
                color="dark"
                startIcon={<Icon>west</Icon>}
                onClick={() => navigate(`/wacc/businesses/${ref}`)}
              >
                Back
              </MDButton>
              <MDButton
                variant="contained"
                color="dark"
                startIcon={<Icon>add</Icon>}
                onClick={() => setCreateOpen(true)}
              >
                New Broadcast
              </MDButton>
            </MDBox>
          </MDBox>

          {/* SEARCH */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card>
                <MDBox p={2}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search broadcasts"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <Icon
                          sx={{
                            mr: 1,
                            color: "text.secondary",
                          }}
                        >
                          search
                        </Icon>
                      ),
                    }}
                  />
                </MDBox>
              </Card>
            </Grid>

            {/* BROADCASTS */}
            <Grid item xs={12}>
              <Card>
                <MDBox p={2}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <MDTypography variant="h6">Broadcasts</MDTypography>

                    <MDTypography variant="caption" color="text">
                      {filteredBroadcasts.length} broadcast
                      {filteredBroadcasts.length === 1 ? "" : "s"}
                    </MDTypography>
                  </MDBox>

                  {filteredBroadcasts.length === 0 ? (
                    <MDBox py={6} textAlign="center">
                      <Icon
                        sx={{
                          fontSize: 42,
                          color: "text.secondary",
                          mb: 1,
                        }}
                      >
                        campaign
                      </Icon>

                      <MDTypography variant="body2" color="text">
                        No broadcasts found
                      </MDTypography>
                    </MDBox>
                  ) : (
                    <MDBox>
                      {filteredBroadcasts.map((broadcast) => (
                        <MDBox
                          key={broadcast.reference}
                          onClick={() => navigate(`/wacc/broadcasts/${broadcast.reference}/view`)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 2,
                            py: 1.5,
                            mb: 1,
                            border: "1px solid #e0e0e0",
                            borderRadius: "4px",
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                            "&:hover": {
                              backgroundColor: "#f8f8f8",
                            },
                          }}
                        >
                          <MDBox
                            sx={{
                              minWidth: 0,
                              flex: 1,
                            }}
                          >
                            <MDTypography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                              }}
                            >
                              {broadcast.title}
                            </MDTypography>

                            <MDTypography
                              sx={{
                                fontSize: "11px",
                                color: "text.secondary",
                                mt: 0.25,
                              }}
                            >
                              {broadcast.created}
                            </MDTypography>
                          </MDBox>

                          <Icon
                            sx={{
                              fontSize: 20,
                              color: "text.secondary",
                            }}
                          >
                            chevron_right
                          </Icon>
                        </MDBox>
                      ))}
                    </MDBox>
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      )}

      <Footer />

      {/* CREATE BROADCAST */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>New Broadcast</DialogTitle>

        <DialogContent>
          <MDBox pt={1}>
            <TextField
              fullWidth
              label="Broadcast name"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              sx={{ mb: 3 }}
            />

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Template</InputLabel>

              <Select
                value={templateReference}
                label="Template"
                onChange={(event) => setTemplateReference(event.target.value)}
                sx={{
                  padding: "12px",
                  paddingLeft: "3px",
                }}
              >
                {templates.map((template) => (
                  <MenuItem key={template.reference} value={template.reference}>
                    {template.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Business number</InputLabel>

              <Select
                value={businessNumberReference}
                label="Business number"
                onChange={(event) => setBusinessNumberReference(event.target.value)}
                sx={{
                  padding: "12px",
                  paddingLeft: "3px",
                }}
              >
                {businessNumbers.map((number) => (
                  <MenuItem key={number.reference} value={number.reference}>
                    {number.title} {number.phone_number ? `(${number.phone_number})` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton variant="outlined" color="dark" onClick={() => setCreateOpen(false)}>
            Cancel
          </MDButton>

          <MDButton color="dark" variant="contained" onClick={handleCreate}>
            Create Broadcast
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default WhatsAppBroadcastsPage;
